import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ImgHTMLAttributes,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
} from 'react';

import { cn } from '@/lib/cn';
import { imageProps, placeholderUrl } from '@/lib/imageRenditions';

/**
 * Every picture a shopper sees on a storefront page, loaded only when it is
 * about to scroll into view.
 *
 * A home page can hold a hero, a slideshow, three product grids, a lookbook
 * and a row of blog teasers. Before this component each one asked for its
 * pictures the moment the page opened, so a phone on a slow connection
 * downloaded the footer's artwork before the hero had finished. Native
 * `loading="lazy"` lets the browser defer anything below the fold, with its
 * own look-ahead margin, and needs no observer of ours.
 *
 * The exception is the top of the page. A lazy hero is *slower*: the browser
 * cannot fetch it until layout says it is visible, which is exactly what
 * Largest Contentful Paint measures. So the first row of sections is wrapped
 * in `AboveTheFold`, and anything a caller knows is the page's main picture -
 * the product photo, the store logo - passes `priority`, which also asks the
 * browser to fetch it ahead of other images.
 *
 * Until a picture arrives its box shows the theme's surface colour, so a grid
 * reads as a grid of cards rather than a column of holes. A lazy laddered
 * upload also shows its blurred 32px sibling over that colour - a few hundred
 * bytes, stretched to the box with the same fit and focal point the picture
 * will use - and swaps to the sharp picture with no fade, since the swap is
 * the transition. Other lazy pictures fade in; eager ones get neither, because
 * nothing should compete with the hero for bandwidth and a fade would only
 * delay the thing the shopper is waiting for. Callers still
 * own the box's size - every slot in the storefront already fixes an aspect
 * ratio or a height - so nothing moves when the picture lands.
 */

const AboveTheFoldContext = createContext(false);

/** Marks its children as drawn on first paint, so their pictures load eagerly. */
export function AboveTheFold({ value = true, children }: { value?: boolean; children: ReactNode }) {
  return <AboveTheFoldContext.Provider value={value}>{children}</AboveTheFoldContext.Provider>;
}

/**
 * Fetches the picture after `current` in a rotating banner, so it is ready by
 * the time the banner turns.
 *
 * A hero or slideshow renders only the picture on show, which keeps the others
 * off the network until they are needed - but "needed" is a fixed number of
 * seconds away, and fetching only then flashes an empty frame on every turn.
 * Warming one ahead keeps the saving on a ten-slide banner nobody watches to
 * the end. The same `srcset` and `sizes` as the `<img>` that will show it, so
 * the browser picks the same rung and the turn is served from its cache.
 */
export function usePreloadNext(urls: readonly string[], current: number, sizes: string, enabled = true) {
  const next = urls.length > 1 ? urls[(current + 1) % urls.length] : '';
  useEffect(() => {
    if (!enabled || !next) return;
    const { src, srcSet } = imageProps(next, sizes);
    const image = new Image();
    if (srcSet) {
      image.sizes = sizes;
      image.srcset = srcSet;
    }
    if (src) image.src = src;
  }, [enabled, next, sizes]);
}

/**
 * Calls back once `element` comes within a screen or two of the viewport.
 *
 * A CSS background is not lazy the way `loading="lazy"` is: every tile on a
 * long page would fetch its placeholder the moment the page opened. One
 * observer is shared by every picture rather than one each, and its margin is
 * close to the look-ahead browsers use for lazy images, so the placeholder is
 * asked for at about the moment the picture is.
 */
const nearCallbacks = new WeakMap<Element, () => void>();
let nearObserver: IntersectionObserver | null = null;

function watchNear(element: Element, onNear: () => void): () => void {
  nearObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        nearCallbacks.get(entry.target)?.();
        nearCallbacks.delete(entry.target);
        nearObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: '1250px 0px' },
  );
  nearCallbacks.set(element, onNear);
  nearObserver.observe(element);
  return () => {
    nearCallbacks.delete(element);
    nearObserver?.unobserve(element);
  };
}

export interface StorefrontImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes' | 'loading'> {
  /** The stored URL. A laddered upload gets a `srcset`; anything else a plain `src`. */
  url: string | null | undefined;
  /** How wide the slot is before layout, for choosing a rung of the ladder. */
  sizes?: string;
  /** The page's main picture: loaded eagerly and fetched ahead of the rest. */
  priority?: boolean;
  /** Fade in once loaded. Off for pictures that animate their own opacity. */
  fade?: boolean;
  /** The colour shown while the picture loads. `false` for none, e.g. a logo. */
  placeholder?: string | false;
  /** For a caller that measures the picture, like the zoom's pan limits. */
  ref?: Ref<HTMLImageElement>;
}

export function StorefrontImage({
  url,
  sizes,
  priority = false,
  fade = true,
  placeholder = 'var(--storefront-surface)',
  className,
  style,
  onLoad,
  ref: forwarded,
  ...rest
}: StorefrontImageProps) {
  const aboveTheFold = useContext(AboveTheFoldContext);
  const eager = priority || aboveTheFold;
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // A picture already in the cache can finish before React has attached its
  // listener, and would sit behind the placeholder for good.
  useEffect(() => {
    const image = ref.current;
    if (image?.complete && image.naturalWidth > 0) setLoaded(true);
  }, [url]);

  // Only below the fold: above it the placeholder would race the picture the
  // page is measured by, and lose nothing by being left out.
  const blurUrl = !eager && placeholder !== false ? placeholderUrl(url) : null;
  // Without IntersectionObserver (old browsers, tests) it is simply shown.
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const image = ref.current;
    if (near || !blurUrl || !image) return;
    return watchNear(image, () => setNear(true));
  }, [near, blurUrl]);
  const blur = near ? blurUrl : null;

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    setLoaded(true);
    onLoad?.(event);
  };

  // Stable, so React does not detach and re-attach the caller's ref on every render.
  const attach = useCallback(
    (node: HTMLImageElement | null) => {
      ref.current = node;
      if (typeof forwarded === 'function') forwarded(node);
      else if (forwarded) forwarded.current = node;
    },
    [forwarded],
  );

  return (
    <img
      ref={attach}
      {...imageProps(url, sizes)}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : undefined}
      onLoad={handleLoad}
      data-loaded={loaded || undefined}
      className={cn(
        fade && !eager && !blurUrl && loaded && 'motion-safe:animate-[storefront-image-in_300ms_ease-out]',
        className,
      )}
      style={
        loaded || placeholder === false
          ? style
          : {
              backgroundColor: placeholder,
              ...(blur && {
                backgroundImage: `url("${blur}")`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: backgroundFit(className, style),
                backgroundPosition: style?.objectPosition ?? 'center',
              }),
              ...style,
            }
      }
      {...rest}
    />
  );
}

/**
 * The `background-size` that lays the placeholder where the picture will land.
 *
 * A product tile shows its photo whole (`object-contain`) and a banner crops
 * it (`object-cover`); a placeholder sized the other way would either spill
 * past the photo's edges or leave bands the photo will fill.
 */
function backgroundFit(className: string | undefined, style: CSSProperties | undefined): string {
  const fit = style?.objectFit ?? (className?.includes('object-contain') ? 'contain' : 'cover');
  return fit === 'contain' || fit === 'scale-down' ? 'contain' : 'cover';
}
